import { useTablePaginationManager } from "@powerledger/ui-component-lib";
import { subYears } from "date-fns";
import { useCallback, useMemo, useState } from "react";

import { defaultFilter, mapStackedFilterStateToFilterState } from "@/app/components/page-header/filter";
import { useAppContext } from "@/app/context";
import { useRecOptions } from "@/app/hooks";
import { getDatePart } from "@/app/lib/date-helpers";
import { TraceXSupportedRegistries } from "@/app/types/okta.types";

import { FilterState, StackedFilterState } from "../../components/page-header/filter/filter.types";
import { formatAttributes, ReturnTypeFormatAttributes } from "../../lib/format-attributes";
import {
  AssetType,
  OrderPosition,
  RecAttributes,
  SortOrderInput,
  useMarketOrdersQuery,
} from "../../types/generated/graphql";
import { MarketTableData, MarketUnSupportedColumns, MarketViewProps } from "./market.types";

export const useMarket = (): MarketViewProps => {
  const [typeFilter, setTypeFilter] = useState<OrderPosition | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>(defaultFilter);
  const [showAllAttributes, setShowAllAttributes] = useState(false);
  const { userAccountInfo, userSelections } = useAppContext();
  const activeRegistryCode = userSelections?.registry?.registryCode as TraceXSupportedRegistries;

  const { pageInfo, offset, fetchData, sort, resetPage, setTotalItems } = useTablePaginationManager<
    MarketTableData,
    SortOrderInput[]
  >({});

  const { data, loading, previousData, refetch } = useMarketOrdersQuery({
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: !activeRegistryCode,
    variables: {
      input: {
        offSetPaginationInfo: {
          offset,
          limit: pageInfo.pageSize,
        },
        sortOrderInputs: sort,
        where: {
          recAttributes: {
            eligibilities: selectedFilters.eligibilities,
            certifications: selectedFilters.certifications,
            locations: selectedFilters.locations,
            fuelSources: selectedFilters.fuelSources,
            vintages: selectedFilters.vintages,
            projects: selectedFilters.projects,
            certificateTypes: selectedFilters.certificateType,
            commencementDate: selectedFilters.cod?.length
              ? getDatePart(subYears(new Date(), +selectedFilters.cod?.[0]))
              : undefined,
          },
          assetType: AssetType.Rec,
          position: typeFilter,
          registryCode: activeRegistryCode,
        },
      },
    },
    onCompleted(data) {
      setTotalItems(data?.marketOrders?.offsetInfo?.total ?? 0);
    },
  });
  const currentData = useMemo(() => data ?? previousData, [data, previousData]);

  const { recOptions, loading: recOptionsLoading } = useRecOptions();

  const handleChangeTypeFilter = useCallback(
    (position: OrderPosition | null) => {
      setTypeFilter(position);
      resetPage();
    },
    [resetPage],
  );

  const tableData = useMemo(() => {
    const marketTableData: MarketTableData[] = [];

    if (!currentData?.marketOrders?.orders) return marketTableData;
    for (const order of currentData.marketOrders.orders) {
      const attributes = order.attributes as RecAttributes;
      const formattedAttributes = recOptions
        ? formatAttributes({
            attributes,
            filteredEligibility: selectedFilters.eligibilities[0],
            position: order.position,
            options: recOptions,
          })
        : ({} as ReturnTypeFormatAttributes);

      marketTableData.push({
        ...order,
        ...formattedAttributes,
        attributes,
      });
    }
    return marketTableData;
  }, [currentData?.marketOrders?.orders, recOptions, selectedFilters.eligibilities]);

  const applyFilters = useCallback(
    (editingFilters: StackedFilterState) => {
      setSelectedFilters(mapStackedFilterStateToFilterState(editingFilters));
      resetPage();
    },
    [resetPage, setSelectedFilters],
  );

  const hideBuyButton = useMemo(() => typeFilter === OrderPosition.Bid, [typeFilter]);
  const hideSellButton = useMemo(() => typeFilter === OrderPosition.Ask, [typeFilter]);

  const assetCounts = useMemo(
    () => ({
      bid: currentData?.marketOrders?.data?.totalBids ?? 0,
      ask: currentData?.marketOrders?.data?.totalAsks ?? 0,
    }),
    [currentData?.marketOrders?.data],
  );

  const reloadMarketOrders = useCallback(() => {
    refetch();
  }, [refetch]);

  const marketHiddenColumn = useMemo(() => {
    const { unsupportedColumns = [], toggleColumns = [] } =
      MarketUnSupportedColumns[userSelections?.registry?.registryCode as TraceXSupportedRegistries];
    return [...unsupportedColumns, ...(showAllAttributes ? [] : toggleColumns)];
  }, [userSelections?.registry?.registryCode, showAllAttributes]);

  return {
    typeFilter,
    handleChangeTypeFilter,
    reloadMarketOrders,
    fetchData,
    pageInfo,
    tableData,
    selectedFilters,
    loading: loading || recOptionsLoading,
    applyFilters,
    hideSellButton,
    assetCounts,
    hideBuyButton,
    recOptions,
    showAllAttributes,
    setShowAllAttributes,
    accountData: {
      id: userAccountInfo?.id,
      tenantId: userAccountInfo?.tenantId,
      type: userAccountInfo?.accountType,
      status: userAccountInfo?.status,
      registryCode: userSelections?.registry?.registryCode as TraceXSupportedRegistries,
    },
    marketHiddenColumn,
  };
};
